import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { faGear } from "@fortawesome/pro-solid-svg-icons";

import { Chip, DropDownMenu, DropDownMenuOption, Icon, Tabs, Text, toast } from "@performyard/py-components";

import Breadcrumbs from "../../../../components/Breadcrumbs";
import ExpandableText from "../../../../components/ExpandableText";
import CompetenciesTab from "./CompetenciesTab";
import EmployeesTab from "./EmployeesTab";
import RoleDetailsModal from "./RoleDetailsModal";

import { rolesKeys, useArchiveRole, useReopenRole, useRole } from "../../../../api/hooks/defs/roles";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { ErrorResponse } from "../../../../api/services/defs/internal-api/internal-base-request";

import { ROLES_LIST_PATH } from "../CreateEditRole/validationSchema";
import ArchiveModal from "../RolesTab/ArchiveModal";

export type RoleDetailTab = "competencies" | "employees";

const VALID_TABS: RoleDetailTab[] = ["competencies", "employees"];

const isValidTab = (value: string | null): value is RoleDetailTab =>
	value !== null && (VALID_TABS as string[]).includes(value);

const RoleDetail: React.FC = () => {
	const { roleId = "" } = useParams<{ roleId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const { value: competenciesEnabled } = useFeatureFlagQuery("ff-is-competency-management-enabled", false);
	const queryClient = useQueryClient();

	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [archiveState, setArchiveState] = useState<{ isOpen: boolean; employeeCount: number }>({
		isOpen: false,
		employeeCount: 0,
	});
	// Reported up from the Competencies tab so the archive confirm can warn before discarding.
	const [pendingLevelEdits, setPendingLevelEdits] = useState(0);
	// Also reported up: while the tab's bulk save is in flight the PATCH can't be cancelled, so an
	// archive would promise to discard edits that are mid-commit — archiving waits it out instead.
	const [isBulkSaving, setIsBulkSaving] = useState(false);

	const tabParam = searchParams.get("tab");
	// Competencies is the default landing tab when enabled; otherwise (and for a stale
	// ?tab=competencies bookmark with the flag off) fall back to Employees.
	const defaultTab: RoleDetailTab = competenciesEnabled ? "competencies" : "employees";
	const activeTab: RoleDetailTab =
		isValidTab(tabParam) && (tabParam !== "competencies" || competenciesEnabled) ? tabParam : defaultTab;

	const { data, isLoading, error } = useRole(roleId);
	const isArchived = data?.role?.status === "archived";
	// HRIS-managed role membership locks the Employees tab; competency editing is unaffected.
	const roleFieldLocked = !!data?.roleFieldIntegrationConnected;

	const { mutate: archiveMutate } = useArchiveRole({
		onSuccess: () => {
			toast.success("Role Archived");
			setArchiveState({ isOpen: false, employeeCount: 0 });
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
		},
		onError: (mutationError) => {
			// Mirrors the roles list page: a blocked archive swaps the modal into its
			// "reassign employees" state instead of a generic failure toast.
			if (
				mutationError instanceof ErrorResponse &&
				mutationError.data?.message === "role_has_assigned_employees"
			) {
				setArchiveState({ isOpen: true, employeeCount: Number(mutationError.data?.meta?.assignedCount) });
				return;
			}
			toast.error(
				mutationError instanceof Error ? mutationError.message : "An error occurred while archiving the role."
			);
			setArchiveState({ isOpen: false, employeeCount: 0 });
		},
	});

	const { mutate: reopenMutate } = useReopenRole({
		onSuccess: () => {
			toast.success("Role Reopened");
			queryClient.invalidateQueries({ queryKey: rolesKeys.all });
		},
		onError: (mutationError) => {
			toast.error(
				mutationError instanceof Error ? mutationError.message : "An error occurred while reopening the role."
			);
		},
	});

	useEffect(() => {
		if (error) toast.error("Role not found");
	}, [error]);

	// Hooks must run unconditionally and in the same order on every render — keep this above
	// the loading/error early returns. Keying on `roleId` (always defined from useParams) instead
	// of `role._id` lets us memoize before `role` has resolved.
	const tabConfig = useMemo(
		() => [
			...(competenciesEnabled
				? [
						{
							value: "competencies",
							label: "Competencies",
							content: (
								<CompetenciesTab
									roleId={roleId}
									readOnly={isArchived}
									onPendingCountChange={setPendingLevelEdits}
									onSavingChange={setIsBulkSaving}
								/>
							),
						},
					]
				: []),
			{
				value: "employees",
				label: "Employees",
				content: <EmployeesTab roleId={roleId} readOnly={isArchived || roleFieldLocked} />,
			},
		],
		[roleId, isArchived, roleFieldLocked, competenciesEnabled]
	);

	if (error) return <Navigate to={ROLES_LIST_PATH} replace />;

	if (isLoading) {
		return <div data-testid="role-detail-loading" />;
	}

	const role = data?.role;
	if (!role) {
		return <div data-testid="role-detail-loading" />;
	}

	const handleTabChange = (next: string) => {
		// A tab switch unmounts the Competencies tab, which would release the archive lock while the
		// bulk PATCH is still committing — hold the tab in place until the save settles (seconds; its
		// selects and exit controls are frozen for the same reason).
		if (isBulkSaving) return;
		const newParams = new URLSearchParams(searchParams);
		if (next === "competencies") {
			newParams.delete("tab");
		} else {
			newParams.set("tab", next);
		}
		setSearchParams(newParams, { replace: false });
	};

	const hasDescription = Boolean(role.description?.trim());

	// Same access as the roles list page's row actions — both are reachable only behind the
	// "roles:viewAdmin" gate the Roles route wrapper already enforces, and the list page has no
	// separate edit/archive permission check of its own to mirror.
	const kebabOptions: DropDownMenuOption[] =
		role.status === "archived"
			? [
					{ key: "edit", label: "Edit Role Details" },
					{ key: "reopen", label: "Reopen Role" },
				]
			: [
					{ key: "edit", label: "Edit Role Details" },
					{ key: "archive", label: "Archive Role", disabled: isBulkSaving },
				];

	const handleKebabAction = (key: string) => {
		if (key === "edit") {
			setIsEditModalOpen(true);
		} else if (key === "archive") {
			setArchiveState({ isOpen: true, employeeCount: role.employeeCount });
		} else if (key === "reopen") {
			reopenMutate(role._id);
		}
	};

	return (
		<div className="px-6 py-8">
			<Breadcrumbs
				pages={[
					{ pageID: "breadcrumb-roles", label: "Roles", url: ROLES_LIST_PATH },
					{ pageID: "breadcrumb-role-name", label: role.name, url: "" },
				]}
				className="mb-4"
			/>

			<div className="mb-6 flex flex-col gap-2">
				<div className="flex items-center gap-3">
					<Text size="sub-heading" weight="semi-bold" color="neutral-9">
						{role.name}
					</Text>
					{role.status === "archived" && <Chip label="Archived" color="neutral" size={20} />}
					<div className="ml-auto">
						{/* Bordered "Options" trigger (review-cycle/goal header pattern), condensed to content
					    width with an 8px text↔icon gap per design. */}
						<DropDownMenu
							menuClassName="w-[180px]"
							bordered
							options={kebabOptions}
							onSelect={handleKebabAction}
							placement="bottom-end"
							testid="role-detail-options-menu"
						>
							<div className="flex items-center gap-2">
								<Text size="paragraph-3" weight="regular" color="neutral-9">
									Options
								</Text>
								<Icon icon={faGear} size={16} color="neutral-7" />
							</div>
						</DropDownMenu>
					</div>
				</div>
				{hasDescription && <ExpandableText content={role.description} />}
			</div>

			<Tabs value={activeTab} onValueChange={handleTabChange} tabs={tabConfig} size="large" />

			{isEditModalOpen && <RoleDetailsModal isOpen role={role} onClose={() => setIsEditModalOpen(false)} />}

			{archiveState.isOpen && (
				<ArchiveModal
					isOpen
					roleName={role.name}
					employeeCount={archiveState.employeeCount}
					pendingEditCount={pendingLevelEdits}
					onConfirm={() => archiveMutate(role._id)}
					onClose={() => setArchiveState({ isOpen: false, employeeCount: 0 })}
					onViewEmployees={() => {
						setArchiveState({ isOpen: false, employeeCount: 0 });
						handleTabChange("employees");
					}}
				/>
			)}
		</div>
	);
};

export default RoleDetail;
